import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { AuthLayout } from '@/layouts/AuthLayout';
import { RegisterManagement } from '../components/RegisterManagement';
import {
  registerUserSchema,
  TRegisterUserSchema,
} from '@/schemas/user/register-user.schema';
import { Loader } from '@/components/Loading/Loader';
import registerCarIllusationUrl from '@/assets/svg/register-car.svg';
import Modal from '@/components/Modal';
import { hideModal, showModal } from '@/utils/modal.utils';
import { useCreateUser } from '@/hooks/user.hooks';
import { getAccessToken } from '@/utils/token.utils';

export const Register = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();

  useEffect(() => {
    if (getAccessToken()) {
      navigate('/');
    }
  }, [navigate]);

  const {
    watch: watchUser,
    control: userControl,
    register: registerUser,
    setValue: setUserValue,
    setError: setUserError,
    handleSubmit: handleSubmitUser,
    formState: { errors: userErrors, isSubmitting: isSubmittingUser },
  } = useForm<TRegisterUserSchema>({
    resolver: zodResolver(registerUserSchema),
  });

  const { mutate: mutateCreateUser, isPending: isPendingCreateUser } =
    useCreateUser({
      t,
      handleSuccess: () => {
        showModal('#confirmation-register-modal');
      },
    });

  const onSubmit = (user: TRegisterUserSchema) => {
    delete user?.passwordConfirm;
    mutateCreateUser(user);
  };

  return (
    <>
      <AuthLayout
        illustrationUrl={registerCarIllusationUrl}
        title={t('common:accountCreation')}
        description={t('fillInFormToContinue')}
      >
        <RegisterManagement<TRegisterUserSchema>
          t={t}
          watch={watchUser}
          control={userControl}
          register={registerUser}
          setValue={setUserValue}
          setError={setUserError}
          errors={userErrors}
          submitDisabled={isSubmittingUser || isPendingCreateUser}
          onSubmit={handleSubmitUser(onSubmit)}
        />
      </AuthLayout>
      <Modal
        id="confirmation-register-modal"
        title={t('common:confirmation')}
        centerY
        size="small"
        showCancelBtn={false}
        showSecondBtn
        secondBtnLabel={t('common:ok')}
        onClickSecondBtn={() => {
          hideModal('#confirmation-register-modal');
          navigate('/login');
        }}
      >
        <div className="flex gap-2">
          <i className="-mt-1.5 ki-filled ki-check-circle text-2xl text-success" />
          <div>{t('accountCreatedSuccessfully')}</div>
        </div>
      </Modal>
      {(isSubmittingUser || isPendingCreateUser) && <Loader />}
    </>
  );
};
